import { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { Lock, X } from 'react-feather';
import { brandTaxComplianceService } from '../../../services/brand-tax-compliance.service';
import { toast } from 'react-hot-toast';
import { Button } from '../../ui/button';

interface ReAuthenticationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ReAuthenticationModal({
  isOpen,
  onClose,
  onSuccess,
}: ReAuthenticationModalProps) {
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await brandTaxComplianceService.reauthenticate(password);
      toast.success('Access confirmed');
      onSuccess();
      setPassword('');
    } catch (error: any) {
      console.error('Re-authentication error:', error);
      toast.error(error?.response?.data?.message || 'Invalid password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-card p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-foreground flex items-center gap-2"
                  >
                    <Lock className="w-5 h-5" />
                    Confirm Access
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-muted-foreground hover:text-muted-foreground"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mt-4">
                  <p className="text-sm text-muted-foreground mb-4">
                    Please enter your password to view sensitive tax information.
                  </p>

                  <form onSubmit={handleSubmit}>
                    <div>
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-muted-foreground mb-1"
                      >
                        Password
                      </label>
                      <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                        placeholder="Enter your password"
                        autoFocus
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <Button
                        type="submit"
                        disabled={!password || isSubmitting}
                      >
                        {isSubmitting ? 'Confirming...' : 'Confirm'}
                      </Button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

